import NiceModal from "@ebay/nice-modal-react";
import { setBaseURL } from "@kaplan-labs/up-auth-api-client";
import { PrismicPreview } from "@prismicio/next/pages";
import { SpeedInsights } from "@vercel/speed-insights/next";
import Head from "next/head";
import Router, { useRouter } from "next/router";

// import { useEffect } from "react";
// import { scan } from "react-scan";

import { gtmScript, pageview } from "@up/analytics";
import { CartProvider } from "@up/checkout";
import { generateLD, getRepositoryName } from "@up/data";
import { SetupModalTriggers } from "@up/lead-capture";

import { FrontendInit } from "./frontend-init";

import type { AppProps } from "next/app";
import type { ComponentType } from "react";

setBaseURL(process.env["NEXT_PUBLIC_HALL_OF_RECORDS_URI"] as string);

// Virtual Pageview for GTM
Router.events.on("routeChangeComplete", (url) => pageview(url));

type SEO = {
  title: string;
  description: string;
  openGraph: {
    site_name: string;
    type: string;
    url: string;
    image: string;
    imageWidth: string;
    imageHeight: string;
  };
  twitter: {
    cardType: string;
  };
};

type AppPropsWithExtras = AppProps & {
  defaultSEO: SEO;
  layoutProps: any;
  Layout: ComponentType<any>;
};

export function CustomNextApp(props: AppPropsWithExtras) {
  const { Component, pageProps, defaultSEO, layoutProps, Layout } = props;
  const router = useRouter();

  // useEffect(() => {
  //   scan({
  //     enabled: true,
  //   });
  // }, []);

  const useGTM =
    process.env.NODE_ENV !== "development" &&
    process.env.NEXT_PUBLIC_GTM_TRACKING_ID;

  const endpoint = process.env.NEXT_PUBLIC_PRISMIC_API_URL as string;
  const repositoryName = getRepositoryName(endpoint);
  const page = pageProps?.page || { data: null };

  // July 23, 2022 — Jerry
  // Checking for null because Prismic currently has a bug
  // with default values not working on select and boolean
  const allowIndexing = (() => {
    if (!page) {
      return false;
    }

    return page.data?.allow_search_indexing === null
      ? true
      : page.data?.allow_search_indexing;
  })();

  const desc = page.data?.description || defaultSEO.description;
  const title = page.data?.title || defaultSEO.title;

  return (
    <>
      <Head>
        {useGTM && (
          <>
            <link rel="preconnect" href="https://www.googletagmanager.com" />
            <script
              id="gtm-script"
              dangerouslySetInnerHTML={{
                __html: gtmScript(
                  process.env.NEXT_PUBLIC_GTM_TRACKING_ID as string,
                ),
              }}
            />
          </>
        )}
        <title key="title">{title}</title>
        <meta key="description" name="description" content={desc} />
        <meta
          key="twitter:card"
          name="twitter:card"
          content={defaultSEO.twitter.cardType}
        />
        <meta
          key="og:site_name"
          property="og:site_name"
          content={defaultSEO.openGraph.site_name}
        />
        <meta
          key="og:url"
          property="og:url"
          content={defaultSEO.openGraph.url}
        />
        <meta
          key="og:type"
          property="og:type"
          content={defaultSEO.openGraph.type}
        />
        <meta key="og:title" property="og:title" content={title} />
        <meta key="og:description" property="og:description" content={desc} />
        <meta
          key="og:image"
          property="og:image"
          content={defaultSEO.openGraph.image}
        />
        <meta
          key="og:image:width"
          property="og:image:width"
          content={defaultSEO.openGraph.imageWidth}
        />
        <meta
          key="og:image:height"
          property="og:image:height"
          content={defaultSEO.openGraph.imageHeight}
        />
        {!allowIndexing && (
          <meta key="robots" name="robots" content="noindex" />
        )}

        <script
          key="ld"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateLD({
                seo: defaultSEO,
                path: props.router.asPath,
                courseData: props.pageProps?.cohortData ?? [],
                courseName: page.data?.course_name
                  ? page.data.course_name
                  : page.data?.is_course_page
                    ? title
                    : "",
                description: desc,
              }),
            ).replace(/</g, "\\u003c"),
          }}
        />
      </Head>

      <FrontendInit />

      <SetupModalTriggers>
        <PrismicPreview repositoryName={repositoryName}>
          <CartProvider
            baseURL={process.env["NEXT_PUBLIC_HALL_OF_RECORDS_URI"] as string}
            router={router}
          >
            <NiceModal.Provider>
              <Layout {...layoutProps}>
                <Component {...pageProps} />
              </Layout>
            </NiceModal.Provider>
          </CartProvider>
        </PrismicPreview>
      </SetupModalTriggers>

      <SpeedInsights />
      <div id="cart-portal" />
      <div id="signin-portal" />
      <div id="cohort-switch-portal" />
    </>
  );
}
